import React, { memo } from "react";
import { ThemeProvider } from "@material-ui/core";
import { createTheme } from "@material-ui/core/styles";
import { alpha, lighten } from "@material-ui/core/styles/colorManipulator";

const backgroundDefaultColor = "#DAE9EB";
const textSecondaryColor = "#232323";
export const globalClasses = {
  filter: "filter",
  filterPaper: "filter-paper",
  textButtonError: "text-button-error",
  filledButtonError: "filled-button-error",
  outlinedButtonError: "outlined-button-error",
};
const baseTheme = {
  palette: {
    type: "light",
    primary: {
      main: "#005C68",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#81C0C4",
      contrastText: "#002E38",
    },
    info: {
      main: "#002E38",
    },
    success: {
      main: "#00FFA4",
    },
    warning: {
      main: "#FFA96E",
    },
    error: {
      main: "#F50057",
    },
    background: {
      default: backgroundDefaultColor,
      paper: "#FFFFFF",
      filter: lighten(backgroundDefaultColor, 0.4),
      disabled: alpha(textSecondaryColor, 0.2),
    },
    text: {
      primary: "#002E38",
      secondary: textSecondaryColor,
      disabled: alpha(textSecondaryColor, 0.3),
    },
    divider: "#ABD5D8",
    common: {
      black: "#000000",
      white: "#FFFFFF",
    },
  },
  custom: {
    topbarHeight: 52,
    sidebarWidth: 200,
  },
};

const overrides = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 961,
      lg: 1281,
      xl: 1920,
    },
  },
  props: {
    MuiListSubheader: {
      disableSticky: true,
    },
  },
  typography: {
    fontFamily: "Nunito, Arial, Helvetica Neue, sans-serif",
  },
  overrides: {
    MuiCssBaseline: {
      "@global": {
        [`.${globalClasses.filter}`]: {
          backgroundColor: baseTheme.palette.background.filter,
        },
        [`.${globalClasses.filterPaper}`]: {
          backgroundColor: baseTheme.palette.background.paper,
        },
        ".Mui-disabled": {
          cursor: "not-allowed !important",
          pointerEvents: "auto !important",
          color: `${baseTheme.palette.text.disabled} !important`,
          "&.MuiButton-contained": {
            backgroundColor: `${baseTheme.palette.background.disabled} !important`,
          },
        },
        ".Mui-error": {
          color: baseTheme.palette.error.main,
        },
        ".MuiAlert-root": {
          backgroundColor: "white !important",
        },
        ".MuiAlert-outlinedSuccess": {
          color: `${baseTheme.palette.primary.main} !important`,
        },
        ".MuiAlert-outlinedInfo": {
          color: `${baseTheme.palette.info.main} !important`,
        },
        ".MuiAlert-outlinedError": {
          color: `${baseTheme.palette.error.main} !important`,
        },
        "@font-face": [
          {
            fontFamily: "Nunito",
            fontStyle: "normal",
            fontDisplay: "swap",
            fontWeight: 400,
            unicodeRange:
              // eslint-disable-next-line max-len
              "U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF",
          },
        ],
      },
    },
    MuiStepLabel: {
      root: {
        color: baseTheme.palette.primary.light,
      },
      label: {
        fontWeight: 500,
      },
    },
    MuiStepIcon: {
      root: {
        color: "inherit",
      },
    },
    MuiTableCell: {
      stickyHeader: {
        backgroundColor: `${baseTheme.palette.background.paper} !important`,
      },
      body: {
        wordBreak: "break-word",
      },
    },
    MuiButtonBase: {
      root: {
        whiteSpace: "nowrap",
      },
    },
    MuiButton: {
      textSecondary: {
        "&:hover": {
          backgroundColor: alpha(
            baseTheme.palette.secondary.main,
            0.15 // ← increase this to make the hover more visible
          ),
        },
      },
    },
    MuiSelect: {
      icon: {
        color: baseTheme.palette.primary.main,
      },
    },
    MuiTableSortLabel: {
      root: {
        "&:hover": {
          color: baseTheme.palette.primary.main,
        },
      },
      active: {
        color: `${baseTheme.palette.primary.main} !important`,
      },
      icon: {
        color: `${baseTheme.palette.primary.main} !important`,
      },
    },
    MuiAutocomplete: {
      popper: {
        zIndex: 10001,
      },
    },
    MuiFormLabel: {
      root: {
        color: baseTheme.palette.text.primary,
      },
    },
  },
};

const theme = createTheme({
  ...baseTheme,
  ...overrides,
});

const textErrorButtonStyles = {
  color: theme.palette.error.main,
  "&:hover": {
    backgroundColor: alpha(
      theme.palette.error.main,
      theme.palette.action.hoverOpacity
    ),
  },
};

const customTheme = createTheme(theme, {
  overrides: {
    MuiCssBaseline: {
      "@global": {
        [`.${globalClasses.textButtonError}`]: {
          ...textErrorButtonStyles,
        },
        [`.${globalClasses.filledButtonError}`]: {
          color: theme.palette.getContrastText(theme.palette.error.main),
          backgroundColor: theme.palette.error.main,
          "&:hover": {
            backgroundColor: theme.palette.error.dark,
          },
        },
        [`.${globalClasses.outlinedButtonError}`]: {
          ...textErrorButtonStyles,
          borderColor: theme.palette.error.main,
        },
      },
    },
  },
});
function CustomThemeProvider({ children }) {
  return <ThemeProvider theme={customTheme}>{children}</ThemeProvider>;
}

export default memo(CustomThemeProvider);
